import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Inline yellow accent bar — magazine aesthetic.
 * Use for inline headings/captions where the heavier <SectionEyebrow> is overkill.
 */
interface YellowAccentBarProps {
  size?: 'sm' | 'md' | 'lg';
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

export function YellowAccentBar({ size = 'md', orientation = 'horizontal', className }: YellowAccentBarProps) {
  const sizeClass =
    orientation === 'horizontal'
      ? size === 'sm'
        ? 'h-[2px] w-4'
        : size === 'lg'
        ? 'h-[2px] w-10'
        : 'h-[2px] w-6'
      : size === 'sm'
      ? 'w-[2px] h-4'
      : size === 'lg'
      ? 'w-[2px] h-10'
      : 'w-[2px] h-6';
  return <span aria-hidden className={cn('inline-block bg-lexsavvy-yellow flex-shrink-0', sizeClass, className)} />;
}

interface EyebrowProps {
  children: React.ReactNode;
  /** Show the leading yellow accent bar. */
  withBar?: boolean;
  size?: 'sm' | 'md';
  tone?: 'foreground' | 'muted';
  as?: 'p' | 'span' | 'div' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  className?: string;
}

/**
 * Lightweight uppercase eyebrow caption.
 * For heading-style eyebrows with description, use <SectionEyebrow> from editorial-section.
 */
export function Eyebrow({
  children,
  withBar = false,
  size = 'md',
  tone = 'muted',
  as: Tag = 'p',
  className,
}: EyebrowProps) {
  const typeClass =
    size === 'sm'
      ? 'text-[10px] font-bold tracking-[0.22em]'
      : 'text-[11px] font-semibold tracking-[0.2em]';
  const toneClass = tone === 'foreground' ? 'text-foreground' : 'text-muted-foreground';
  const content = (
    <Tag className={cn('uppercase', typeClass, toneClass, !withBar && className)}>
      {children}
    </Tag>
  );
  if (!withBar) return content;
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <YellowAccentBar size="sm" />
      {content}
    </div>
  );
}
